import React, { useState } from "react";
import {
  ArrowLeft,
  ChevronRight,
  User,
  UserCheck,
  Building,
  Briefcase,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Footer from "../Footer/Footer";
import Navbar from "../Header/Navbar";
import { useLocation, useNavigate } from "react-router-dom";

export const SelectRegister = () => {
  const [hoveredCard, setHoveredCard] = useState<any>(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showSeasonRoles, setShowSeasonRoles] = useState(
    () =>
      Boolean(
        (location.state as { showSeasonRoles?: boolean } | null)?.showSeasonRoles
      )
  );

  const roles = [
    {
      id: "player",
      title: t("register_form.role_options.player"),
      description: t("select_register.descriptions.player"),
      icon: User,
      path: "/register/player",
      state: { specialRegistration: true },
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-400",
      accentColor: "bg-blue-500",
      disabled: false,
    },
    {
      id: "coach",
      title: t("register_form.role_options.coach"),
      description: t("select_register.descriptions.coach"),
      icon: UserCheck,
      path: "/register/5",
      gradient: "from-green-500/20 to-emerald-500/20",
      iconColor: "text-green-400",
      accentColor: "bg-green-500",
      disabled: false,
    },
    {
      id: "agent",
      title: t("register_form.role_options.agent"),
      description: t("select_register.descriptions.agent"),
      icon: Briefcase,
      path: "/register/4",
      gradient: "from-orange-500/20 to-amber-500/20",
      iconColor: "text-orange-400",
      accentColor: "bg-orange-500",
      disabled: true,
    },
    {
      id: "club",
      title: t("register_form.role_options.club"),
      description: t("select_register.descriptions.club"),
      icon: Building,
      path: "/register/1",
      gradient: "from-purple-500/20 to-violet-500/20",
      iconColor: "text-purple-400",
      accentColor: "bg-purple-500",
      disabled: false,
    },
  ];

  const seasonAccountTypes = [
    {
      label: t("register_form.role_options.player"),
      icon: User,
      className: "text-blue-200/85",
    },
    {
      label: t("register_form.role_options.coach"),
      icon: UserCheck,
      className: "text-emerald-200/85",
    },
    {
      label: t("register_form.role_options.club"),
      icon: Building,
      className: "text-violet-200/85",
    },
  ];

  const handleCardClick = (role: any) => {
    if (role.disabled) return;

    setSelectedCard(role.id);
    navigate(role.path, role.state ? { state: role.state } : undefined);
  };

  const handleBackToSeasonCard = () => {
    setHoveredCard(null);
    setSelectedCard(null);
    setShowSeasonRoles(false);
    navigate("/register-selection", { replace: true, state: null });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-cyan-500/5 to-transparent rounded-full"></div>
      </div>

      <Navbar isRegister={true} />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-20">
        {showSeasonRoles && (
          <div className="mb-10 flex w-full max-w-7xl flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white md:text-4xl">
                {t("select_register.subscription_spain_2026_2027")}
              </h1>
              <p className="mt-3 text-base text-white/65">
                {t("select_register.choose_account_type")}
              </p>
            </div>

            <button
              type="button"
              onClick={handleBackToSeasonCard}
              className="inline-flex w-fit items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("select_register.common.go_back")}
            </button>
          </div>
        )}

        {!showSeasonRoles && (
          <button
            type="button"
            onClick={() => {
              setShowSeasonRoles(true);
              navigate("/register-selection", {
                replace: true,
                state: { showSeasonRoles: true },
              });
            }}
            className="group relative w-full max-w-4xl cursor-pointer overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] p-0 text-center shadow-2xl shadow-black/30 backdrop-blur-xl transition-all duration-500 hover:-translate-y-1 hover:border-white/25 hover:bg-white/[0.08]"
          >
            <div className="absolute inset-x-0 top-0 flex h-2">
              <div className="flex-1 bg-red-600"></div>
              <div className="flex-[2] bg-yellow-400"></div>
              <div className="flex-1 bg-red-600"></div>
            </div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(250,204,21,0.14),transparent_36%),linear-gradient(135deg,rgba(37,99,235,0.16),transparent_38%,rgba(16,185,129,0.12))] opacity-80 transition-opacity duration-500 group-hover:opacity-100"></div>
            <div className="absolute left-8 right-8 top-1/2 h-px bg-white/5"></div>
            <div className="absolute left-1/2 top-10 h-[calc(100%-5rem)] w-px bg-white/5"></div>
            <div className="absolute -left-20 top-1/2 h-44 w-44 -translate-y-1/2 rounded-full border border-white/10"></div>
            <div className="absolute -right-20 top-1/2 h-44 w-44 -translate-y-1/2 rounded-full border border-white/10"></div>
            <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5"></div>

            <div className="relative z-10 flex min-h-[340px] flex-col items-center justify-center px-8 py-12 md:px-14">
              <div className="mb-8 inline-flex h-20 w-20 items-center justify-center rounded-3xl bg-white/10 shadow-xl shadow-black/20 ring-1 ring-white/15 transition-all duration-500 group-hover:bg-white/20 group-hover:scale-105">
                <div
                  className="h-10 w-14 overflow-hidden rounded-md shadow-lg ring-1 ring-white/25 transition-transform duration-500 group-hover:scale-110"
                  aria-label={"Bandera de Espa\u00f1a"}
                  role="img"
                >
                  <div className="h-2.5 bg-red-600"></div>
                  <div className="h-5 bg-yellow-400"></div>
                  <div className="h-2.5 bg-red-600"></div>
                </div>
              </div>

              <h2 className="max-w-3xl text-4xl font-bold leading-tight text-white md:text-5xl">
                {t("select_register.subscription_spain_2026_2027")}
              </h2>

              <div
                className="mt-8 flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-white/70"
                aria-hidden="true"
              >
                {seasonAccountTypes.map((accountType) => {
                  const AccountIcon = accountType.icon;

                  return (
                    <span
                      key={accountType.label}
                      className="inline-flex items-center gap-2 text-sm font-semibold"
                    >
                      <AccountIcon
                        className={`h-4 w-4 ${accountType.className}`}
                        strokeWidth={1.8}
                      />
                      <span>{accountType.label}</span>
                    </span>
                  );
                })}
              </div>

              <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-5 py-3 text-sm font-semibold text-white/80 shadow-lg shadow-black/20 transition-all duration-300 group-hover:border-white/25 group-hover:bg-white/15 group-hover:text-white">
                <span>{t("select_register.common.get_started")}</span>
                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </div>
          </button>
        )}

        {showSeasonRoles && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto w-full">
            {roles.map((role) => {
              const IconComponent = role.icon;
              const isHovered = hoveredCard === role.id;
              const isSelected = selectedCard === role.id;

              return (
                <div
                  key={role.id}
                  className={`group relative transition-all duration-500 ease-out transform ${
                    role.disabled ? "cursor-not-allowed opacity-45" : "cursor-pointer"
                  } ${isHovered && !role.disabled ? "scale-105 -translate-y-2" : ""} ${
                    isSelected ? "scale-95" : ""
                  }`}
                  onMouseEnter={() => !role.disabled && setHoveredCard(role.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  onClick={() => handleCardClick(role)}
                  aria-disabled={role.disabled}
                >
                  <div
                    className={`relative bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 overflow-hidden transition-all duration-500 ${
                      isHovered ? "bg-white/10 border-white/20" : ""
                    } ${isSelected ? "bg-white/20 border-white/30" : ""}`}
                  >
                    <div
                      className={`absolute inset-0 bg-gradient-to-br ${role.gradient} opacity-0 transition-opacity duration-500 ${
                        isHovered ? "opacity-100" : ""
                      }`}
                    ></div>

                    <div
                      className={`absolute top-0 left-0 right-0 h-1 ${role.accentColor} transform scale-x-0 transition-transform duration-500 ${
                        isHovered ? "scale-x-100" : ""
                      }`}
                    ></div>

                    <div className="relative z-10">
                      <div
                        className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 mb-6 transition-all duration-500 ${
                          isHovered ? "bg-white/20 scale-110" : ""
                        }`}
                      >
                        <IconComponent
                          className={`w-8 h-8 ${role.iconColor} transition-all duration-500 ${
                            isHovered ? "scale-110" : ""
                          }`}
                        />
                      </div>

                      <h3 className="text-xl font-bold text-white mb-3 transition-colors duration-300">
                        {role.title}
                      </h3>

                      <p className="text-white/60 text-sm leading-relaxed mb-6 min-h-[5rem]">
                        {role.description}
                      </p>

                      <div
                        className={`flex items-center justify-between text-white/70 text-sm transition-all duration-300 ${
                          isHovered ? "text-white" : ""
                        }`}
                      >
                        <span className="font-medium">
                          {role.disabled
                            ? t("select_register.common.not_available")
                            : t("select_register.common.get_started")}
                        </span>
                        <ChevronRight
                          className={`w-4 h-4 transition-all duration-300 ${
                            isHovered && !role.disabled ? "translate-x-1" : ""
                          }`}
                        />
                      </div>
                    </div>

                    <div
                      className={`absolute inset-0 rounded-2xl transition-all duration-500 ${
                        isHovered
                          ? `shadow-2xl shadow-${role.accentColor.split("-")[1]}-500/20`
                          : ""
                      }`}
                    ></div>
                  </div>

                  <div
                    className={`absolute inset-0 rounded-2xl border-2 transition-all duration-300 ${
                      isSelected
                        ? `border-${role.accentColor.split("-")[1]}-400`
                        : "border-transparent"
                    }`}
                  ></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};
